import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { colors } from '../variables'

import arrow from '../img/icons/ub-pil-90-orange-rgb.svg'

type Props = {
  children: any
  className?: React.CSSProperties
}

export const MainTextHolder = ({ children, className }: Props) => {
  return <MainText className={className}>{children}</MainText>
}

export const SubHeader = ({
  shade,
  children,
  style,
}: {
  shade?: string
  children: any
  style?: React.CSSProperties
}) => {
  return (
    <StyledH2 style={style} shade={shade}>
      {children}
    </StyledH2>
  )
}

const StyledH2 = styled.h2`
  color: ${({ shade }: { shade: string }) =>
    shade === 'dark' ? colors.green : colors.white};
  margin-bottom: 15px;
  margin-top: 30px;

  @media (min-width: 769px) {
    margin-top: 0;
  }
`

export const ImgSet = ({
  img,
  style,
}: {
  img: string
  style?: React.CSSProperties
}) => {
  const name = img.substring(0, img.lastIndexOf('.'))
  const suffix = img.substring(img.lastIndexOf('.'))

  return (
    <img
      src={img}
      data-srcset={`${img} 1x, ${name}@2x${suffix}, ${name}@3x${suffix}`}
      alt=""
      style={style}
    />
  )
}

export const Ingress = styled.p`
  font-size: 15px;
  line-height: 21px;
  color: ${({ shade }: { shade: string }) =>
    shade === 'dark' ? colors.green : colors.white};

  @media (min-width: 769px) {
    font-size: 18px;
    line-height: 1.45;
  }
`

export const ButtonMain = styled.button`
  padding: 10px 40px 10px 15px;
  border: 2px solid ${colors.green};
  color: ${colors.green};
  font-family: Gelion;
  font-weight: 600;
  font-size: 18px;
  position: relative;
  margin-top: 30px;

  &:after {
    position: absolute;
    right: 15px;
    top: 11px;
    width: 15px;
    height: 20px;
    background-image: url('${arrow}');
    background-repeat: no-repeat;
    z-index: 1;
    content: '';
  }
`

const MainText = styled.div`
  text-align: center;
  padding: 40px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    width: 980px;
    max-width: 100%;
    margin: 0px auto;
    padding: 60px;
  }

  h4 {
    margin-top: 10px;
    margin-bottom: 4px;
    font-size: 17px;
  }

  h3 {
    font-size: 32px;
    line-height: 40px;
    color: #fff;

    @media (min-width: 769px) {
      font-size: 42px;
      line-height: 50px;
    }
  }

  > h4 {
    margin-top: 0;
  }

  figure {
    width: 100%;
    margin: 0;

    > img {
      width: 100%;
    }
  }
`

export const MainHolder = styled(motion.div)`
  width: 100vw;
  max-width: 1200px;
  margin: 0 auto;
  margin-top: 60px;

  h4 {
    text-transform: uppercase;
  }

  h3 {
    font-size: 60px;
    line-height: 68px;
    color: #fff;
  }

  strong {
    font-weight: 700;
  }

  p {
    margin-bottom: 20px;
    margin-top: 0;
  }
`

export const MainLeft = styled.div`
  width: calc(50% - 30px);
  margin-right: 30px;
  position: relative;

  img {
    position: absolute;
  }
`

export const MainRight = styled.div`
  width: calc(50% - 30px);
  margin-left: 30px;
  position: relative;

  img {
    position: absolute;
  }
`

export const StyledImage = styled.img`
  width: 100%;
`

export const Spacer = styled.div`
  width: 100%;
`

export const Payoff = styled.div`
  font-size: 17px;
  line-height: 1.55;
  width: 100%;
  max-width: 570px;
  font-family: 'Miriam Libre';
  user-select: none;
  color: ${({ shade }: { shade: string }) =>
    shade === 'dark' ? colors.green : colors.white};

  @media (min-width: 769px) {
    font-size: 22px;
  }
`

export const getSlug = (link: string) => {
  if (link.substr(link.length - 1) === '/') {
    link = link.substr(0, link.length - 1)
  }
  link = link.substr(link.lastIndexOf('/'))

  return link
}

export const Loading = () => {
  return (
    <Spinner>
      <div className="rect1"></div>
      <div className="rect2"></div>
      <div className="rect3"></div>
      <div className="rect4"></div>
      <div className="rect5"></div>
    </Spinner>
  )
}

export const PixelStairsTopLeft = styled.section`
  width: 100%;

  > div:first-child {
    position: absolute;
    bottom: 80px;
    left: 80px;
    height: 40px;
    width: calc(100% - 80px);
    background-color: ${({ color }: { color: any }) => color};

    @media (min-width: 769px) {
      left: 280px;
      bottom: 280px;
      height: 140px;
      width: calc(100% - 280px);
    }
  }

  > div:nth-child(2) {
    position: absolute;
    left: 40px;
    bottom: 40px;
    height: 40px;
    width: calc(100% - 40px);
    background-color: ${({ color }: { color: any }) => color};

    @media (min-width: 769px) {
      bottom: 140px;
      left: 140px;
      height: 140px;
      width: calc(100% - 140px);
    }
  }

  > div:nth-child(3) {
    position: absolute;
    height: 40px;
    bottom: 0;
    width: 100%;
    background-color: ${({ color }: { color: any }) => color};

    @media (min-width: 769px) {
      height: 140px;
      left: 0;
    }
  }
`

export const PixelStairsContent = styled.div`
  display: flex;
  flex-direction: row;
  position: absolute;
  align-items: flex-start;
  justify-content: flex-end;
  position: absolute;
  right: 70px;
  bottom: 40px;
`

const Spinner = styled.div`
  margin: 100px auto;
  width: 50px;
  height: 40px;
  text-align: center;
  font-size: 10px;

  > div {
    background-color: #aaa;
    height: 100%;
    width: 6px;
    display: inline-block;
    margin: 2px;

    animation: sk-stretchdelay 1.2s infinite ease-in-out;

    &.rect2 {
      animation-delay: -1.1s;
    }

    &.rect3 {
      animation-delay: -1s;
    }

    &.rect4 {
      animation-delay: -0.9s;
    }

    &.rect5 {
      animation-delay: -0.8s;
    }
  }

  @keyframes sk-stretchdelay {
    0%,
    40%,
    100% {
      transform: scaleY(0.4);
      -webkit-transform: scaleY(0.6);
    }
    20% {
      transform: scaleY(1);
      -webkit-transform: scaleY(1);
    }
  }
`

export const scrollToDiv = (pageLink: string, offset?: number) => {
  var element = document.getElementById(pageLink) || document.body
  var headerOffset = offset || 80
  var elementPosition = element.offsetTop
  var offsetPosition = elementPosition - headerOffset

  window.scrollTo({
    top: offsetPosition,
    left: 0,
    behavior: 'smooth',
  })
}

export const MainArea = styled.div`
  width: 100vw;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
`

export const ImageBlock = styled.div`
  position: absolute;

  > img {
    position: absolute;
  }
`

export const PageWrapper = styled.section`
  padding: 0 40px;
  display: flex;

  @media (min-width: 769px) {
    padding: 0 40px;
  }
`
